import { array } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { CheckInConfigurationType, CheckInConfigurationValue } from '@mews-commander/data-access';
import { CheckInFieldsValues } from '../types';
import { documentFields, documentSectionFields } from '../sections';
import { getCheckInSameValues } from '../utils';

/* we only care about reservationOwner value for the documentType fields */
export const getSelectedDocumentType = (values: CheckInFieldsValues): CheckInConfigurationType | null => {
    const isEverythingDefault = pipe(
        documentSectionFields,
        array.every((field) => values[field].reservationOwner === CheckInConfigurationValue.Default)
    );

    if (isEverythingDefault) {
        return null;
    }

    const nonDoNotShowField = documentSectionFields.find(
        (fieldType) => values[fieldType].reservationOwner !== CheckInConfigurationValue.DoNotShow
    );

    return (
        nonDoNotShowField ??
        // The reason we return PassportOrIdentityCardOrDriversLicense is that it covers everything that's DoNotShow
        CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense
    );
};

const resetDocumentFields = () => {
    const resettedFields = pipe(
        documentFields,
        array.map(({ type }) => [type, getCheckInSameValues(CheckInConfigurationValue.Default)]),
        Object.fromEntries
    );

    const resettedDocumentTypesFields = pipe(
        documentSectionFields,
        array.map((type) => [type, getCheckInSameValues(CheckInConfigurationValue.Default)]),
        Object.fromEntries
    );

    return { ...resettedFields, ...resettedDocumentTypesFields };
};

const getDocumentTypeChanges = flow(
    (selectedDocumentType: CheckInConfigurationType) => {
        switch (selectedDocumentType) {
            case CheckInConfigurationType.PassportOrIdentityCard:
                return [
                    CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense,
                    CheckInConfigurationType.DirectPassport,
                    CheckInConfigurationType.DirectIdentityCard,
                ];
            case CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense:
                return [
                    CheckInConfigurationType.PassportOrIdentityCard,
                    CheckInConfigurationType.DirectPassport,
                    CheckInConfigurationType.DirectIdentityCard,
                    CheckInConfigurationType.DirectDriversLicense,
                ];

            case CheckInConfigurationType.DirectPassport:
            case CheckInConfigurationType.DirectIdentityCard:
                return [
                    CheckInConfigurationType.PassportOrIdentityCard,
                    CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense,
                ];
            case CheckInConfigurationType.DirectDriversLicense:
                return [CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense];
            default:
                return [];
        }
    },
    array.map((documentType) => [documentType, getCheckInSameValues(CheckInConfigurationValue.DoNotShow)]),
    Object.fromEntries
);

export const changeDocumentVisibility = (
    selectedDocumentType: CheckInConfigurationType | null,
    fields: CheckInFieldsValues,
    visibility: CheckInConfigurationValue
) => {
    /** Changing document visibility of null is not allowed by UI — the Visibility field is disabled */
    if (selectedDocumentType == null) {
        return fields;
    }

    const isValueRequiredOrOptional = [CheckInConfigurationValue.Required, CheckInConfigurationValue.Optional].includes(
        visibility
    );
    const visibilityObject = getCheckInSameValues(visibility);

    if (isValueRequiredOrOptional) {
        const changes = getDocumentTypeChanges(selectedDocumentType);
        return { ...fields, ...changes, [selectedDocumentType]: visibilityObject };
    }

    return { ...fields, [selectedDocumentType]: visibilityObject };
};

export const changeDocumentType = (documentType: CheckInConfigurationType | null, fields: CheckInFieldsValues) =>
    documentType == null ? { ...fields, ...resetDocumentFields() } : fields;
